import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import LocationBottomNav from '../components/LocationBottomNav';
import LocationTrades from './location/LocationTrades';
import LocationMyShop from './location/LocationMyShop';
import LocationNotifications from './location/LocationNotifications';
import LocationStaff from './location/LocationStaff';
import LocationScoreboard from './location/LocationScoreboard';

// Icons
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const StoreIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
    <path d="M2 7h20" />
    <path d="M22 7v3a2 2 0 0 1-2 2 2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />
  </svg>
);

const LocationManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { theme } = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [myLocation, setMyLocation] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [activeTab, setActiveTab] = useState('trades');
  const [reviewStats, setReviewStats] = useState(null);

  // Fetch user's location and role
  useEffect(() => {
    const fetchMyLocation = async () => {
      if (!activeProfile?.id) return;

      try {
        const { data: staffRecord, error } = await supabase
          .from('location_staff')
          .select(`
            role,
            status,
            can_add_staff,
            locations (
              id,
              store_name,
              address,
              city,
              state,
              phone,
              website,
              description,
              photo_url,
              photos,
              verified,
              visible_on_app,
              operating_hours,
              subscription_tier
            )
          `)
          .eq('user_id', activeProfile.id)
          .in('status', ['active', 'pending'])
          .single();

        if (error) {
          console.log('[LocationMgmt] No location found for user');
          setMyLocation(null);
          setLoading(false);
          return;
        }

        if (staffRecord?.locations) {
          setMyLocation(staffRecord.locations);
          setMyRole(staffRecord.role);
        }
      } catch (err) {
        console.error('[LocationMgmt] Error fetching location:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMyLocation();
  }, [activeProfile?.id]);

  // Fetch reviews for the location
  useEffect(() => {
    const fetchReviews = async () => {
      if (!myLocation?.id) return;

      try {
        const { data: ratingsData } = await supabase
          .from('location_ratings')
          .select('cleanliness_rating, friendliness_rating, space_rating, staff_helped, staff_rating, created_at, reviewer_id, users!reviewer_id(username, avatar_url)')
          .eq('location_id', myLocation.id)
          .order('created_at', { ascending: false });

        if (ratingsData && ratingsData.length > 0) {
          let totalCleanliness = 0, countCleanliness = 0;
          let totalFriendliness = 0, countFriendliness = 0;
          let totalSpace = 0, countSpace = 0;
          let totalStaff = 0, countStaff = 0;

          ratingsData.forEach(r => {
            if (r.cleanliness_rating) { totalCleanliness += r.cleanliness_rating; countCleanliness++; }
            if (r.friendliness_rating) { totalFriendliness += r.friendliness_rating; countFriendliness++; }
            if (r.space_rating) { totalSpace += r.space_rating; countSpace++; }
            if (r.staff_helped && r.staff_rating) { totalStaff += r.staff_rating; countStaff++; }
          });

          const avgCleanliness = countCleanliness > 0 ? totalCleanliness / countCleanliness : 0;
          const avgFriendliness = countFriendliness > 0 ? totalFriendliness / countFriendliness : 0;
          const avgSpace = countSpace > 0 ? totalSpace / countSpace : 0;
          const avgStaff = countStaff > 0 ? totalStaff / countStaff : 0;
          const overallAvg = (avgCleanliness + avgFriendliness + avgSpace) / 3;

          setReviewStats({
            avg: overallAvg,
            count: ratingsData.length,
            breakdown: {
              cleanliness: avgCleanliness,
              friendliness: avgFriendliness,
              space: avgSpace,
              staff: avgStaff,
              staffCount: countStaff,
            },
            recentReviews: ratingsData.slice(0, 5),
          });
        }
      } catch (err) {
        console.error('[LocationMgmt] Error fetching reviews:', err);
      }
    };

    fetchReviews();
  }, [myLocation?.id]);

  // Get tab titles for header
  const getTabTitle = () => {
    switch (activeTab) {
      case 'trades': return 'Upcoming Trades';
      case 'shop': return 'My Shop';
      case 'notifications': return 'Notifications';
      case 'staff': return 'Staff';
      case 'scoreboard': return 'Scoreboard';
      default: return 'Manage Location';
    }
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'trades':
        return <LocationTrades location={myLocation} />;
      case 'shop':
        return <LocationMyShop location={myLocation} />;
      case 'notifications':
        return <LocationNotifications location={myLocation} />;
      case 'staff':
        return <LocationStaff location={myLocation} role={myRole} />;
      case 'scoreboard':
        return <LocationScoreboard location={myLocation} />;
      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={{ ...styles.header, backgroundColor: theme.bg }}>
          <button style={{ ...styles.backButton, color: theme.text }} onClick={() => navigate(-1)}>
            <BackIcon />
          </button>
          <h1 style={{ ...styles.headerTitle, color: theme.text }}>Manage Location</h1>
          <div style={{ ...styles.headerBorder, backgroundColor: theme.border }} />
        </div>
        <div style={styles.loadingContainer}>
          <div style={{ ...styles.spinner, borderColor: theme.border, borderTopColor: '#8b5cf6' }} />
        </div>
      </div>
    );
  }

  // No location found
  if (!myLocation) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={{ ...styles.header, backgroundColor: theme.bg }}>
          <button style={{ ...styles.backButton, color: theme.text }} onClick={() => navigate(-1)}>
            <BackIcon />
          </button>
          <h1 style={{ ...styles.headerTitle, color: theme.text }}>Manage Location</h1>
          <div style={{ ...styles.headerBorder, backgroundColor: theme.border }} />
        </div>
        <div style={styles.emptyState}>
          <div style={{ color: theme.textSecondary }}>
            <StoreIcon />
          </div>
          <h3 style={{ ...styles.emptyTitle, color: theme.text }}>No Location Found</h3>
          <p style={{ ...styles.emptyDesc, color: theme.textSecondary }}>
            You're not associated with any card shop location.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, backgroundColor: theme.bg }}>
      {/* Header */}
      <div style={{ ...styles.header, backgroundColor: theme.bg }}>
        <button style={{ ...styles.backButton, color: theme.text }} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <div style={styles.headerContent}>
          <h1 style={{ ...styles.headerTitle, color: theme.text }}>{getTabTitle()}</h1>
          <span style={{ ...styles.headerSubtitle, color: theme.textSecondary }}>
            {myLocation.store_name}
          </span>
        </div>
        <span style={styles.roleBadge}>
          {myRole === 'owner' ? 'Owner' : myRole === 'admin' ? 'Admin' : 'Staff'}
        </span>
        <div style={{ ...styles.headerBorder, backgroundColor: theme.border }} />
      </div>

      {/* Reviews Summary Card - Only in My Shop tab */}
      {activeTab === 'shop' && reviewStats && (
        <div style={{ ...styles.reviewsCard, backgroundColor: theme.bgCard, borderColor: theme.border }}>
          <div style={styles.reviewsHeader}>
            <div style={styles.reviewsOverall}>
              <span style={{ ...styles.reviewsAvg, color: theme.text }}>{reviewStats.avg.toFixed(1)}</span>
              <div style={styles.reviewsStars}>
                {[1, 2, 3, 4, 5].map(star => (
                  <svg key={star} width="16" height="16" viewBox="0 0 24 24" fill={star <= Math.round(reviewStats.avg) ? "#fbbf24" : "none"} stroke="#fbbf24" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                ))}
              </div>
              <span style={{ ...styles.reviewsCount, color: theme.textSecondary }}>{reviewStats.count} reviews</span>
            </div>
            <div style={styles.reviewsBreakdown}>
              <div style={styles.reviewsBarRow}>
                <span style={{ ...styles.reviewsBarLabel, color: theme.textSecondary }}>Cleanliness</span>
                <div style={{ ...styles.reviewsBarTrack, backgroundColor: theme.border }}>
                  <div style={{ ...styles.reviewsBarFill, width: `${(reviewStats.breakdown.cleanliness / 5) * 100}%`, backgroundColor: '#8b5cf6' }} />
                </div>
                <span style={{ ...styles.reviewsBarValue, color: theme.text }}>{reviewStats.breakdown.cleanliness.toFixed(1)}</span>
              </div>
              <div style={styles.reviewsBarRow}>
                <span style={{ ...styles.reviewsBarLabel, color: theme.textSecondary }}>Friendliness</span>
                <div style={{ ...styles.reviewsBarTrack, backgroundColor: theme.border }}>
                  <div style={{ ...styles.reviewsBarFill, width: `${(reviewStats.breakdown.friendliness / 5) * 100}%`, backgroundColor: '#8b5cf6' }} />
                </div>
                <span style={{ ...styles.reviewsBarValue, color: theme.text }}>{reviewStats.breakdown.friendliness.toFixed(1)}</span>
              </div>
              <div style={styles.reviewsBarRow}>
                <span style={{ ...styles.reviewsBarLabel, color: theme.textSecondary }}>Space</span>
                <div style={{ ...styles.reviewsBarTrack, backgroundColor: theme.border }}>
                  <div style={{ ...styles.reviewsBarFill, width: `${(reviewStats.breakdown.space / 5) * 100}%`, backgroundColor: '#8b5cf6' }} />
                </div>
                <span style={{ ...styles.reviewsBarValue, color: theme.text }}>{reviewStats.breakdown.space.toFixed(1)}</span>
              </div>
              {reviewStats.breakdown.staffCount > 0 && (
                <div style={styles.reviewsBarRow}>
                  <span style={{ ...styles.reviewsBarLabel, color: theme.textSecondary }}>Staff</span>
                  <div style={{ ...styles.reviewsBarTrack, backgroundColor: theme.border }}>
                    <div style={{ ...styles.reviewsBarFill, width: `${(reviewStats.breakdown.staff / 5) * 100}%`, backgroundColor: '#8b5cf6' }} />
                  </div>
                  <span style={{ ...styles.reviewsBarValue, color: theme.text }}>{reviewStats.breakdown.staff.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div style={styles.content}>
        {renderTabContent()}
      </div>

      {/* Bottom Navigation */}
      <LocationBottomNav activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
  },
  header: {
    position: 'sticky',
    top: 0,
    padding: '16px',
    paddingTop: 'calc(16px + env(safe-area-inset-top))',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    zIndex: 100,
  },
  headerBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 1,
  },
  backButton: {
    background: 'none',
    border: 'none',
    padding: 8,
    margin: -8,
    cursor: 'pointer',
  },
  headerContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 12,
    margin: 0,
  },
  roleBadge: {
    fontSize: 10,
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: 6,
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    color: '#8b5cf6',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  content: {
    minHeight: 'calc(100vh - 120px)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginTop: 16,
    marginBottom: 8,
  },
  emptyDesc: {
    fontSize: 14,
    maxWidth: 280,
  },
  // Reviews Card Styles
  reviewsCard: {
    margin: '0 16px 16px',
    padding: 16,
    borderRadius: 12,
    border: '1px solid',
  },
  reviewsHeader: {
    display: 'flex',
    gap: 20,
  },
  reviewsOverall: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 80,
  },
  reviewsAvg: {
    fontSize: 32,
    fontWeight: 700,
  },
  reviewsStars: {
    display: 'flex',
    gap: 2,
    marginTop: 4,
  },
  reviewsCount: {
    fontSize: 12,
    marginTop: 4,
  },
  reviewsBreakdown: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  reviewsBarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  reviewsBarLabel: {
    fontSize: 12,
    minWidth: 80,
  },
  reviewsBarTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  reviewsBarFill: {
    height: '100%',
    borderRadius: 3,
  },
  reviewsBarValue: {
    fontSize: 12,
    fontWeight: 600,
    minWidth: 24,
    textAlign: 'right',
  },
};

export default LocationManagement;
